import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  getAllPaymentReceipts,
  createPaymentReceipt,
  updatePaymentReceipt,
  approvePaymentReceipt,
  rejectPaymentReceipt,
  getAllEngagements,
  getAllUsers,
} from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Modal, { ConfirmModal } from '../../components/ui/Modal';
import { PAYMENT_MODE, RECEIPT_STATUS, formatDate, formatINR, label } from '../../constants/garmentCodes';
import useCreateIntent, { CREATE_INTENT } from '../../customHooks/useCreateIntent';
import PaymentReceiptForm, { STEP_LABELS } from '../../components/forms/PaymentReceiptForm';
import { isManufacturerRole } from '../../utils/userRole';

const STATUS = { SUBMITTED: 2, UNDER_REVIEW: 3, NEEDS_INFO: 4, POSTED: 6, REJECTED: 7 };
const REVIEWABLE = [STATUS.SUBMITTED, STATUS.UNDER_REVIEW, STATUS.NEEDS_INFO];
const REJECTION_REASONS = [
  'Payment not located',
  'Incorrect amount',
  'Invalid transaction reference',
  'Duplicate submission',
  'Unclear screenshot',
  'Wrong manufacturer account',
  'Other',
];
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';

const ageInDays = (date) => {
  if (!date) return '—';
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
  if (Number.isNaN(days)) return '—';
  return days <= 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`;
};

const byId = (list) => Object.fromEntries(list.map((row) => [String(row.id), row]));

function ReviewDrawer({ receipt, engagement, submitter, onClose, onApprove, onReject, onRequestInfo }) {
  if (!receipt) return null;
  const status = label(RECEIPT_STATUS, receipt.status);
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[480px] max-w-full bg-white shadow-2xl overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-[14px] font-semibold text-gray-900">{receipt.receiptNumber || `Receipt #${receipt.id}`}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="flex-1 p-6 space-y-5">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[12px] text-gray-500">Brand</p>
                <p className="text-[14px] font-semibold text-gray-900">{engagement?.brandName || '—'}</p>
                <p className="text-[11px] text-gray-400">{engagement?.name}</p>
              </div>
              <Badge status={status}>{status}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <div><p className="text-gray-400">Amount</p><p className="font-bold font-mono text-[18px] text-gray-900 mt-0.5">{formatINR(receipt.amount)}</p></div>
              <div><p className="text-gray-400">Submitted</p><p className="font-medium text-gray-700 mt-0.5">{formatDate(receipt.submittedDate)}</p></div>
              <div><p className="text-gray-400">Payment Date</p><p className="font-medium text-gray-700 mt-0.5">{formatDate(receipt.paymentDate)}</p></div>
              <div><p className="text-gray-400">Payment Mode</p><p className="font-medium text-gray-700 mt-0.5">{label(PAYMENT_MODE, receipt.mode)}</p></div>
              <div><p className="text-gray-400">Transaction ID</p><p className="font-mono text-[12px] text-gray-800 mt-0.5">{receipt.transactionId || '—'}</p></div>
              <div><p className="text-gray-400">UTR</p><p className="font-mono text-[12px] text-gray-800 mt-0.5">{receipt.utr || '—'}</p></div>
            </div>
          </div>

          <div>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Submitted By</p>
            <p className="text-[13px] text-gray-800">{submitter?.name || '—'}</p>
            <p className="text-[11px] text-gray-400">{ageInDays(receipt.submittedDate)} ago</p>
          </div>

          <div>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Attachments</p>
            <p className="text-[12px] text-gray-700">{receipt.attachments ?? 0} file(s) attached</p>
          </div>

          {receipt.rejectionReason && (
            <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-[12px] text-red-700">{receipt.rejectionReason}</div>
          )}

          {engagement && (
            <div className="bg-green-50 border border-green-100 rounded-lg p-4">
              <p className="text-[11px] font-medium text-green-700 uppercase tracking-wide mb-2">Wallet Impact (if approved)</p>
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                <div><p className="text-green-600">Current Balance</p><p className="font-mono font-semibold text-gray-800">{formatINR(engagement.walletBalance)}</p></div>
                <div><p className="text-green-600">After Approval</p><p className="font-mono font-semibold text-green-700">{formatINR((engagement.walletBalance || 0) + (receipt.amount || 0))}</p></div>
              </div>
            </div>
          )}
        </div>

        {REVIEWABLE.includes(receipt.status) && (
          <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
            <button onClick={onReject} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50">Reject</button>
            {receipt.status !== STATUS.NEEDS_INFO && (
              <button onClick={onRequestInfo} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-50">Need Info</button>
            )}
            <button onClick={onApprove} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Approve & Credit</button>
          </div>
        )}
      </div>
    </div>
  );
}

function RejectModal({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const confirm = () => {
    onConfirm([reason, note.trim()].filter(Boolean).join(' — '));
    setReason('');
    setNote('');
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose} title="Reject Payment Receipt" size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Rejection Reason *</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls}>
            <option value="">Select reason</option>
            {REJECTION_REASONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Note to brand</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={inputCls} />
        </div>
        <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-[12px] text-red-700">The wallet will not be credited.</div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={confirm} disabled={!reason} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">Confirm Rejection</button>
        </div>
      </div>
    </Modal>
  );
}


export default function PaymentReceipts() {
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state.auth?.loginUser);
  const [params] = useSearchParams();
  const engagementId = params.get('engagementId') || '';
  const userId = useSelector((state) => state.auth?.loginUser?.userId ?? null);
  const receipts = useSelector((state) => state.paymentreceipt.receipts);
  const loading = useSelector((state) => state.paymentreceipt.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const users = useSelector((state) => state.user.users);
  const engagementMap = useMemo(() => byId(engagements), [engagements]);
  const userMap = useMemo(() => byId(users), [users]);

  const [selected, setSelected] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [receiptStep, setReceiptStep] = useState(1);
  useCreateIntent(CREATE_INTENT.RECEIPT, () => setAddOpen(true));

  useEffect(() => {
    dispatch(getAllEngagements());
    dispatch(getAllUsers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllPaymentReceipts({ engagementId, status: '' }));
  }, [dispatch, engagementId]);

  const counts = receipts.reduce(
    (acc, r) => {
      acc.total += 1;
      if (REVIEWABLE.includes(r.status)) acc.pending += 1;
      if (r.status === STATUS.POSTED) acc.approved += 1;
      return acc;
    },
    { total: 0, pending: 0, approved: 0 },
  );

  const approve = () => {
    if (!selected) return;
    dispatch(approvePaymentReceipt({ id: selected.id, engagementId: selected.engagementId, status: STATUS.POSTED, ledgerType: 2, ledgerCategory: 2 }));
    setSelected(null);
  };

  const reject = (reason) => {
    if (!selected) return;
    dispatch(rejectPaymentReceipt({ id: selected.id, status: STATUS.REJECTED, rejectionReason: reason }));
    setSelected(null);
  };

  const requestInfo = () => {
    if (!selected) return;
    dispatch(updatePaymentReceipt({ payload: { id: selected.id, status: STATUS.NEEDS_INFO } }));
    setSelected(null);
  };

  const handleCreate = (body) => {
    dispatch(createPaymentReceipt({ payload: body }));
    setAddOpen(false);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Header title="Payment Receipts" breadcrumbs={[{ workspace: true }, { label: 'Payment Receipts' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Total Receipts</p>
            <p className="text-[22px] font-bold text-gray-900">{counts.total}</p>
          </div>
          <div className="bg-amber-50 rounded-lg border border-amber-100 p-4">
            <p className="text-[10px] text-amber-600 uppercase tracking-wide mb-1">Pending Review</p>
            <p className="text-[22px] font-bold text-amber-700">{counts.pending}</p>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-100 p-4">
            <p className="text-[10px] text-green-600 uppercase tracking-wide mb-1">Posted to Wallet</p>
            <p className="text-[22px] font-bold text-green-700">{counts.approved}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
          {!isManufacturerRole(loginUser) && (
            <button onClick={() => setAddOpen(true)} className="ml-auto px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
              + Add Receipt
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Payment Receipts"
            columns={[
              { id: 'receiptNum', label: 'RECEIPT #', align: 'left' },
              { id: 'brand', label: 'BRAND', align: 'left' },
              { id: 'submitted', label: 'SUBMITTED', align: 'left' },
              { id: 'paymentDate', label: 'PAYMENT DATE', align: 'left' },
              { id: 'amount', label: 'AMOUNT', align: 'right' },
              { id: 'mode', label: 'MODE', align: 'left' },
              { id: 'transactionId', label: 'TRANSACTION ID', align: 'left' },
              { id: 'submittedBy', label: 'SUBMITTED BY', align: 'left' },
              { id: 'age', label: 'AGE', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'actions', label: 'ACTIONS', align: 'left', disableFilter: true },
            ]}
            rows={receipts.map((receipt) => {
              const eng = engagementMap[String(receipt.engagementId)];
              const status = label(RECEIPT_STATUS, receipt.status);
              const submitter = userMap[String(receipt.submittedByUserId)]?.name || '—';
              const open = () => !receipt.__optimistic && setSelected(receipt);
              return {
                id: receipt.id,
                rColumns: [
                  <div key="num" label={receipt.receiptNumber || ''} onClick={open} className={`flex items-center w-full h-full px-2 py-1 font-mono text-[12px] text-[#1b3a6b] font-medium cursor-pointer ${receipt.__optimistic ? 'opacity-60' : ''}`}>{receipt.receiptNumber || '—'}</div>,
                  <div key="brand" label={eng?.brandName || ''} onClick={open} className="flex flex-col justify-center items-start w-full h-full px-2 py-1 cursor-pointer">
                    <p className="text-[12px] font-medium text-gray-800">{eng?.brandName || '—'}</p>
                    <p className="text-[10px] text-gray-400">{eng?.name || ''}</p>
                  </div>,
                  <div key="sub" label={receipt.submittedDate || ''} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600 cursor-pointer">{formatDate(receipt.submittedDate)}</div>,
                  <div key="pay" label={receipt.paymentDate || ''} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600 cursor-pointer">{formatDate(receipt.paymentDate)}</div>,
                  <div key="amt" label={formatINR(receipt.amount)} onClick={open} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[13px] font-semibold text-gray-900 cursor-pointer">{formatINR(receipt.amount)}</div>,
                  <div key="mod" label={label(PAYMENT_MODE, receipt.mode)} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600 cursor-pointer">{label(PAYMENT_MODE, receipt.mode)}</div>,
                  <div key="txn" label={receipt.transactionId || ''} onClick={open} className="flex items-center w-full h-full px-2 py-1 font-mono text-[11px] text-gray-500 cursor-pointer">{receipt.transactionId || receipt.utr || '—'}</div>,
                  <div key="sby" label={submitter} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600 cursor-pointer">{submitter}</div>,
                  <div key="age" label={ageInDays(receipt.submittedDate)} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-500 cursor-pointer">{ageInDays(receipt.submittedDate)}</div>,
                  <div key="stat" label={status} onClick={open} className="flex items-center w-full h-full px-2 py-1 cursor-pointer"><Badge status={status}>{status}</Badge></div>,
                  <div key="act" label="" className="flex items-center w-full h-full px-2 py-1 gap-2">
                    {REVIEWABLE.includes(receipt.status) && !receipt.__optimistic && (
                      <>
                        <button onClick={() => { setSelected(receipt); setApproveOpen(true); }} className="text-[11px] text-green-600 font-medium hover:underline">Approve</button>
                        <button onClick={() => { setSelected(receipt); setRejectOpen(true); }} className="text-[11px] text-red-500 hover:underline">Reject</button>
                      </>
                    )}
                  </div>,
                ],
              };
            })}
          />
          {!loading && receipts.length === 0 && <p className="py-10 text-center text-[12px] text-gray-400">No payment receipts</p>}
        </div>
      </main>

      {selected && !rejectOpen && !approveOpen && (
        <ReviewDrawer
          receipt={selected}
          engagement={engagementMap[String(selected.engagementId)]}
          submitter={userMap[String(selected.submittedByUserId)]}
          onClose={() => setSelected(null)}
          onApprove={() => setApproveOpen(true)}
          onReject={() => setRejectOpen(true)}
          onRequestInfo={requestInfo}
        />
      )}

      <ConfirmModal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={() => {
          approve();
          setApproveOpen(false);
        }}
        title="Approve Payment Receipt"
        message={`Approve ${selected?.receiptNumber || ''} for ${selected ? formatINR(selected.amount) : ''}? A RECHARGE credit is posted to the wallet. This cannot be undone.`}
        confirmLabel="Approve & Credit Wallet"
      />

      <RejectModal open={rejectOpen} onClose={() => setRejectOpen(false)} onConfirm={reject} />

      <Modal
        open={addOpen}
        onClose={() => { setAddOpen(false); setReceiptStep(1); }}
        title="Submit Payment Receipt"
        subtitle={`Step ${receiptStep} of 4 — ${STEP_LABELS[receiptStep - 1]}`}
        size="lg"
      >
        <PaymentReceiptForm
          engagements={engagements}
          defaultEngagementId=""
          userId={userId}
          onSubmit={handleCreate}
          onCancel={() => setAddOpen(false)}
          onStepChange={setReceiptStep}
        />
      </Modal>
    </div>
  );
}
